/**
 * The grouped queue's pure half — owner rulings DEC-101 and DEC-105, `docs/adr/9042`.
 *
 * Every queue follows the alert groups' model: the queue is read as GROUPS (one line per value of
 * a dimension the planner chooses, «Raggruppa per»), a group opens on its filter, and an act takes
 * the whole group, or its narrowed set, in ONE request whose `expectedCount` is the count the
 * planner confirmed. The platform answers with what it applied and what it skipped, each skip
 * labelled in the caller's language; a set that changed since it was counted is a 409 naming the
 * current count, and a set above the platform's bound is a 422 naming both counts.
 *
 * What is here holds for every such queue — the alert inbox (ADR 9040) and the write-back pack and
 * backlog (ADR 9042) — and nothing queue-specific: each queue keeps its own filter, its own
 * problem codes and its own words.
 */

import { ApiProblemError } from '../api';

/** Groups drawn on an inbox, and rows drawn inside a group: ADR 9034's bound. */
export const GROUP_LIMIT = 50;

/** The platform's localised text: a stable key and its words for the caller's language. */
export interface GroupLocalisedText {
  readonly key: string;
  readonly text: string;
}

/** One reason the platform skipped part of a set, with its count and its words. */
export interface GroupActSkip {
  readonly count: number;
  readonly label: GroupLocalisedText;
}

/** What every group act answers: how many it applied, and what it skipped and why. */
export interface GroupActAnswer {
  readonly applied: number;
  readonly skipped: readonly GroupActSkip[];
}

/**
 * Whether a write's refusal is the given problem. The platform namespaces its codes
 * (`planning.foundations.alert_group_changed`), so the tail is what is compared.
 */
export function isGroupProblem(error: unknown, status: number, code: string): error is ApiProblemError {
  if (!(error instanceof ApiProblemError) || error.status !== status) return false;
  return error.code === code || error.code.endsWith(`.${code}`);
}

/** A whole, non-negative member of a problem document, or `null`. */
export function problemWholeMember(error: ApiProblemError, member: string): number | null {
  const value = error.problem[member];
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/** The current count a 409 (or a 422) states — `currentCount` — or `null` without one. */
export function groupCurrentCount(error: ApiProblemError): number | null {
  return problemWholeMember(error, 'currentCount');
}

/** The bound a 422 states — `maximumCount` — or `null`. */
export function groupMaximumCount(error: ApiProblemError): number | null {
  return problemWholeMember(error, 'maximumCount');
}

/** Whole days between `iso` and `now`, never negative; `0` for an unreadable instant. */
export function daysSince(iso: string, now: number): number {
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, Math.floor((now - at) / (24 * 60 * 60 * 1000)));
}

/** A dimension named on the link, or the queue's default when the link names none it offers. */
export function readGroupDimension<D extends string>(
  raw: string | null, offered: readonly D[], fallback: D,
): D {
  return raw !== null && (offered as readonly string[]).includes(raw) ? raw as D : fallback;
}

/** The count a set skipped, over every reason. */
export function skippedTotal(answer: GroupActAnswer): number {
  return answer.skipped.reduce((sum, entry) => sum + entry.count, 0);
}
